import { useEffect, useState } from 'react';
import { isNil } from 'lodash';
import { useFormContext } from 'react-hook-form';
import { useSnapshot } from 'valtio/react';

import { Button } from '@paid-ui/components/button';
import { EmailInput } from '@paid-ui/components/email-input';
import { FormControl, FormField, FormHelper, FormItem } from '@paid-ui/components/form';
import { Select, SelectItem } from '@paid-ui/components/select';
import { BusinessType } from '@paid-ui/enums/business';
import { useUserEmailCheck } from '@paid-ui/queries/user-email-check';

import { TermsCheckbox } from '../../components/terms-checkbox';
import { businessTypeOptions } from '../../constants/business';
import { signUpManager } from '../../models';
import { SignUpStep } from '../../models/enums';

interface Props {
  disabled?: boolean;
  goToStep?: (step: SignUpStep, replace?: boolean) => void;
}

export const StepTypeAndEmail: React.FC<Props> = ({ disabled, goToStep }) => {
  const form = useFormContext();
  const { currentStep, invitedSignUp, userGroupId } = useSnapshot(signUpManager);
  const [email, setEmail] = useState('');
  const { data, refetch, isFetching } = useUserEmailCheck({ email });

  const type = form.watch('type');
  const _email = form.watch('email');
  const agreedOnTerms = form.watch('agreedOnTerms');
  const isValid = Boolean(type && _email && agreedOnTerms) && !form.getFieldState('email').invalid;

  const handleNext = () => {
    if (invitedSignUp) {
      goToStep?.(SignUpStep.BUSINESS_STRUCTURE);
    } else {
      setEmail(form.getValues('email'));
      form.trigger('email');
      setTimeout(refetch, 100);
    }
  };

  useEffect(() => {
    if (userGroupId) {
      goToStep?.(SignUpStep.USER_DETAILS, true);
      return;
    }

    if (isNil(data)) {
      return;
    }

    if (data) {
      form.setFocus('email');
      form.setError('email', {
        type: 'manual',
        message: 'The email has been registered, please try another.',
      });
    } else {
      switch (type) {
        case BusinessType.OWNER: {
          form.trigger('email');
          goToStep?.(SignUpStep.OWNER_TYPE);
          break;
        }
        default: {
          form.trigger('email');
          goToStep?.(SignUpStep.BUSINESS_STRUCTURE);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isFetching, userGroupId]);

  if (currentStep !== SignUpStep.TYPE_AND_EMAIL) {
    return null;
  }

  return (
    <div className="space-y-9">
      <div className="space-y-3.5">
        <FormField
          name="type"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Select
                  {...field}
                  placeholder="What best describes you?"
                  onValueChange={(value) => form.setValue('type', value)}
                  disabled={disabled}
                >
                  {businessTypeOptions.map((option, index) => (
                    <SelectItem key={index} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </Select>
              </FormControl>
              <FormHelper />
            </FormItem>
          )}
        />
        <FormField
          name="email"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <EmailInput
                  {...field}
                  placeholder="Enter email"
                  autoComplete="off"
                  disabled={disabled || invitedSignUp}
                />
              </FormControl>
              <FormHelper />
            </FormItem>
          )}
        />
      </div>
      <div className="grid gap-3.5">
        <Button
          variant="solid"
          className="h-[44px]"
          disabled={disabled || !isValid}
          loading={isFetching}
          onClick={handleNext}
        >
          {isFetching ? 'Checking...' : 'Continue'}
        </Button>
        <TermsCheckbox />
      </div>
    </div>
  );
};
